import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";

import ClaudePracticeTestAdmin from "./ClaudePracticeTestAdmin";

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString() : "—";

const formatDuration = (seconds) => {
  const value = Number(seconds || 0);
  if (!value) return "—";
  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  return hours ? `${hours}h ${minutes}m` : `${minutes}m`;
};

const statusTone = (status) => {
  if (status === "completed") return "success";
  if (status === "timed_out") return "warning";
  return "info";
};

const MetricCard = ({ label, value, helper }) => (
  <Card variant="outlined" sx={{ height: "100%" }}>
    <CardContent>
      <Typography color="text.secondary" variant="body2">{label}</Typography>
      <Typography variant="h4" sx={{ my: 0.5 }}>{value}</Typography>
      <Typography color="text.secondary" variant="caption">{helper}</Typography>
    </CardContent>
  </Card>
);

const ExamSimulationsAdmin = () => {
  const token = localStorage.getItem("token");
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );
  const [simulations, setSimulations] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attemptsLoading, setAttemptsLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("all");

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/exam-simulations/overview`,
        { headers }
      );
      const next = Array.isArray(response.data?.simulations)
        ? response.data.simulations
        : [];
      setSimulations(next);
      setSelectedSlug((current) =>
        next.some((item) => item.slug === current)
          ? current
          : next[0]?.slug || ""
      );
    } catch (requestError) {
      setError(
        requestError.response?.data?.message ||
          "Unable to load exam simulation analytics."
      );
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const loadAttempts = useCallback(async () => {
    if (!selectedSlug) {
      setAttempts([]);
      return;
    }
    setAttemptsLoading(true);
    setError("");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/exam-simulations/${encodeURIComponent(selectedSlug)}/attempts`,
        {
          headers,
          params: { search: appliedSearch, status: appliedStatus },
        }
      );
      setAttempts(
        Array.isArray(response.data?.attempts)
          ? response.data.attempts
          : []
      );
    } catch (requestError) {
      setError(
        requestError.response?.data?.message ||
          "Unable to load participant attempts."
      );
    } finally {
      setAttemptsLoading(false);
    }
  }, [appliedSearch, appliedStatus, headers, selectedSlug]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    loadAttempts();
  }, [loadAttempts]);

  const selected = simulations.find((item) => item.slug === selectedSlug);

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <Box>
          <Typography variant="h4">Exam Simulations</Typography>
          <Typography color="text.secondary">
            Manage school and professional simulations, participation, attempts, and performance.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={loadOverview}>Refresh analytics</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Simulation catalog</Typography>
          <Grid container spacing={2}>
            {simulations.map((simulation) => (
              <Grid item xs={12} md={6} key={simulation.slug}>
                <Card
                  variant="outlined"
                  onClick={() => setSelectedSlug(simulation.slug)}
                  sx={{
                    cursor: "pointer",
                    borderColor:
                      selectedSlug === simulation.slug
                        ? "primary.main"
                        : "divider",
                    bgcolor:
                      selectedSlug === simulation.slug
                        ? "action.selected"
                        : "background.paper",
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                      <Typography variant="h6">{simulation.title}</Typography>
                      <Chip
                        size="small"
                        label={simulation.status}
                        color={simulation.status === "active" ? "success" : "default"}
                      />
                    </Box>
                    <Typography color="text.secondary" sx={{ my: 1 }}>
                      {simulation.description}
                    </Typography>
                    <Typography variant="caption">
                      Audience: {simulation.audienceType} · Bank: {simulation.bankVersion || "—"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
            {!simulations.length && (
              <Grid item xs={12}>
                <Alert severity="info">No exam simulations are configured.</Alert>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {selected && (
        <>
          <Typography variant="h5" sx={{ mb: 2 }}>{selected.title} analytics</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="Logged-in visitors"
                value={selected.uniqueVisitors}
                helper={`${selected.totalVisits} total page visits`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="Test takers"
                value={selected.uniqueTestTakers}
                helper={`${selected.totalAttempts} attempts started`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="Submitted"
                value={selected.submitted}
                helper={`${selected.inProgress} still in progress · ${selected.timedOut} timed out`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="Average score"
                value={`${selected.averageScore}%`}
                helper={`${selected.readinessRate}% reached the readiness target`}
              />
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} lg={7}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>Domain performance</Typography>
                  {selected.domainPerformance?.length ? (
                    selected.domainPerformance.map((domain) => (
                      <Box key={domain.key} sx={{ mb: 1.5 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                          <Typography variant="body2">{domain.label}</Typography>
                          <Typography variant="body2" fontWeight={700}>
                            {domain.averagePercentage}%
                          </Typography>
                        </Box>
                        <Box sx={{ height: 8, bgcolor: "grey.200", borderRadius: 4, overflow: "hidden" }}>
                          <Box
                            sx={{
                              width: `${domain.averagePercentage}%`,
                              height: "100%",
                              bgcolor:
                                domain.averagePercentage >= 75
                                  ? "success.main"
                                  : "warning.main",
                            }}
                          />
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Typography color="text.secondary">
                      Domain averages will appear after the first submitted attempt.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} lg={5}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6">Operational summary</Typography>
                  <Typography sx={{ mt: 2 }}>
                    Average completion time: <strong>{formatDuration(selected.averageDurationSeconds)}</strong>
                  </Typography>
                  <Typography sx={{ mt: 1 }}>
                    Completion rate:{" "}
                    <strong>
                      {selected.totalAttempts
                        ? Math.round((selected.submitted / selected.totalAttempts) * 100)
                        : 0}%
                    </strong>
                  </Typography>
                  <Typography sx={{ mt: 1 }}>
                    Attempts per test taker:{" "}
                    <strong>
                      {selected.uniqueTestTakers
                        ? (selected.totalAttempts / selected.uniqueTestTakers).toFixed(1)
                        : "0.0"}
                    </strong>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2 }}>
                <Box>
                  <Typography variant="h6">Participant attempts</Typography>
                  <Typography color="text.secondary" variant="body2">
                    Search by participant name or email and review every attempt.
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <TextField
                    size="small"
                    label="Search participant"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                  <TextField
                    select
                    size="small"
                    label="Status"
                    value={status}
                    onChange={(event) => setStatus(event.target.value)}
                    sx={{ minWidth: 150 }}
                  >
                    <MenuItem value="all">All statuses</MenuItem>
                    <MenuItem value="in_progress">In progress</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="timed_out">Timed out</MenuItem>
                  </TextField>
                  <Button
                    variant="contained"
                    onClick={() => {
                      const nextSearch = search.trim();
                      if (
                        nextSearch === appliedSearch &&
                        status === appliedStatus
                      ) {
                        loadAttempts();
                      } else {
                        setAppliedSearch(nextSearch);
                        setAppliedStatus(status);
                      }
                    }}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>

              {attemptsLoading ? (
                <CircularProgress size={28} />
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Participant</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Attempt</TableCell>
                        <TableCell>Score</TableCell>
                        <TableCell>Progress</TableCell>
                        <TableCell>Started / duration</TableCell>
                        <TableCell>Domain scores</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {attempts.map((attempt) => (
                        <TableRow key={attempt.attemptId}>
                          <TableCell>
                            <strong>{attempt.participantName}</strong>
                            <br />
                            <Typography variant="caption">{attempt.participantEmail}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={statusTone(attempt.status)}
                              label={attempt.status.replace("_", " ")}
                            />
                          </TableCell>
                          <TableCell>#{attempt.attemptNumber}</TableCell>
                          <TableCell>
                            {attempt.percentage == null ? "—" : (
                              <>
                                <strong>{attempt.percentage}%</strong>
                                <br />
                                <Typography variant="caption">
                                  {attempt.ready ? "Readiness target met" : "Below readiness target"}
                                </Typography>
                              </>
                            )}
                          </TableCell>
                          <TableCell>
                            {attempt.answeredCount}/{attempt.questionCount} answered
                            <br />
                            <Typography variant="caption">
                              {attempt.skippedCount} skipped · {attempt.flaggedCount} flagged
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {formatDateTime(attempt.startedAt)}
                            <br />
                            <Typography variant="caption">{formatDuration(attempt.durationSeconds)}</Typography>
                          </TableCell>
                          <TableCell>
                            {attempt.domainScores?.length
                              ? attempt.domainScores
                                  .map((domain) => `${domain.label}: ${domain.percentage}%`)
                                  .join(" · ")
                              : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                      {!attempts.length && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            No attempts match the selected filters.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {selected.slug === "claude-architect-foundations" && (
            <Box>
              <Typography variant="h5" sx={{ mb: 2 }}>Simulation configuration</Typography>
              <ClaudePracticeTestAdmin />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ExamSimulationsAdmin;
